import uniqueId from "lodash.uniqueid";
import { isNumber } from "util";
import { Store } from "../../../../../../flux";
import { assertString, assertTrue } from "../../../../../utils/assert";
import { PressureLevels, TemperatureLevels } from "../../../../../../rest/GroupAction";
import { Unit, Timezone } from "../../../../../../rest/Misc";
import { names } from "./VehicleBatchEditActions";

export enum ActionType
{
    AddTags,
    RemoveTags,
    SetTimezone,
    SetPressure,
    SetTemperature,
}

export interface BatchAction<T>
{
    id: string; // needed for local delete
    type: T;
    value: any;
    meta?: any; // in some cases value property has incomplete information for rendering (e.g. UnitId without UnitName)
}

export interface VehicleBatchEditStoreState
{
    selectedAction?: ActionType;
    actionValue?: any;
    unit: Unit;
    
    ids: number[];

    // for pressure and temperature
    axleCount: number;

    // for Add tags control
    allTags: string[];

    // for Remove tags control
    tagsStat: Map<string, number>;
    availableTags: string[]; // depends on tagsStat
    suggestedTags: string[]; // depends on tagsStat

    addedActions: BatchAction<ActionType>[];

    isGroupActionValid: boolean;
}

export class VehicleBatchEditStore extends Store<VehicleBatchEditStoreState>
{
    constructor()
    {
        super();
        this.state = this.getDefaultState();

        this.register<number[]>(names.SET_IDS, e => {
            const { addedActions = [] } = this.getState();
            return this.field({
                ids: e.payload,
                availableTags: [],
                suggestedTags: [],
            });
        });
        this.register<number>(names.SET_AXLE_COUNT, e => this.field({ axleCount: e.payload }));
        this.register<Map<string, number>>(names.SET_AVAILABLE_TAGS_STAT, e => {
            const tagsStat = e.payload;
            const tags = calcTags(tagsStat);
            return this.field({ tagsStat, availableTags: tags.available, suggestedTags: tags.suggested });
        })
        this.register<string[]>(names.SET_ALL_TAGS, e => this.field({ allTags: e.payload }));
        this.register<ActionType>(names.SELECT_ACTION, e => this.field({
            selectedAction: e.payload,
            actionValue: this.getDefaultActionValue(e.payload),
            isGroupActionValid: false,
            unit: null
        }));
        this.register<string[]>(names.SET_SELECTED_TAGS, e => {
            if(this.state.selectedAction === ActionType.RemoveTags) {
                const selectedTags = e.payload;
                const tagsSet = new Set<string>(selectedTags);
                const tagsStat = new Map([...this.state.tagsStat].filter(kv => !tagsSet.has(kv[0])));                
                const tags = calcTags(tagsStat);

                return this.field({ actionValue: selectedTags, availableTags: tags.available, suggestedTags: tags.suggested, isGroupActionValid: Array.isArray(selectedTags) && selectedTags.length > 0 });
            } else if(this.state.selectedAction === ActionType.AddTags) {
                const tags = e.payload;
                return this.field({ actionValue: tags, isGroupActionValid: Array.isArray(tags) && tags.length > 0 });
            }
        });
        this.register<Timezone>(names.SET_TIMEZONE, e => this.field({ actionValue: e.payload, isGroupActionValid: e.payload != null }))
        this.register<Unit>(names.SET_UNIT, e => this.field({ unit: e.payload }));
        
        const isRel = () => this.state.selectedAction === ActionType.SetPressure;
        this.register<number>(names.SET_AXLE, e => {
            const actionValue = { ...this.state.actionValue, Axle: e.payload };
            return this.field({
                actionValue,
                isGroupActionValid: isRel() ? isPressureLevelsValid(actionValue) : isTemperatureLevelsValid(actionValue)
            })
        });
        this.register<number>(names.SET_BASELINE, e => this.pressureLevels({ Baseline: e.payload }));
        this.register<boolean>(names.SET_DEFINE_ALERT_LEVELS_FLAG, e => this.pressureLevels({ DefineAlertLevels: e.payload }));
        this.register<number>(names.SET_RED_MAX, e => isRel() ? this.pressureLevels({ RelLevelMaximumRed: e.payload }) : this.temperatureLevels({ AbsLevelMaximumRed: e.payload }));
        this.register<number>(names.SET_YELLOW_MAX, e => isRel() ? this.pressureLevels({ RelLevelMaximumYellow: e.payload }) : this.temperatureLevels({AbsLevelMaximumYellow: e.payload}));
        this.register<number>(names.SET_YELLOW_MIN, e => isRel() ? this.pressureLevels({ RelLevelMinimumYellow: e.payload }) : this.temperatureLevels({AbsLevelMinimumYellow: e.payload}));
        this.register<number>(names.SET_RED_MIN, e => isRel() ? this.pressureLevels({ RelLevelMinimumRed: e.payload }) : this.temperatureLevels({ AbsLevelMinimumRed: e.payload }));
        
        this.register(names.COMMIT_ACTION, e => {
            const {
                ids = [],
                addedActions: actions = [],
                selectedAction: type,
                actionValue: value,
                tagsStat,
                unit
            } = this.getState();
            let newActions = actions;
            let newSelectedAction = type;
            let newActionValue = value;
            let meta = null;
            if(type === ActionType.SetPressure || type === ActionType.SetTemperature) {
                (value as (TemperatureLevels | PressureLevels)).UnitId = unit.Id;
                meta = unit.Name;
                if(type === ActionType.SetTemperature){
                    if(
                        (value.AbsLevelMinimumRed < value.AbsLevelMinimumYellow) &&
                        (value.AbsLevelMinimumYellow < value.AbsLevelMaximumYellow) &&
                        (value.AbsLevelMaximumYellow < value.AbsLevelMaximumRed)
                    ){
                        newActions = [...actions, { type, value, id: uniqueId(), meta }];
                        newActionValue = null;
                        newSelectedAction = null;
                    }else{
                        alert('Inconsitent values - Red levels must be higher than Yellow levels');
                    }
                }else if(type === ActionType.SetPressure){
                    if(
                        (value.DefineAlertLevels == true) &&
                        (value.RelLevelMaximumRed > value.RelLevelMaximumYellow) &&
                        (value.RelLevelMinimumRed > value.RelLevelMinimumYellow)
                    ){
                        newActions = [...actions, { type, value, id: uniqueId(), meta }];
                        newActionValue = null;
                        newSelectedAction = null;
                    }else if(
                        (value.DefineAlertLevels == false)
                    ){
                        newActions = [...actions, { type, value, id: uniqueId(), meta }];
                        newActionValue = null;
                        newSelectedAction = null;
                    }else{
                        alert('Inconsitent values - Red levels must be higher than Yellow levels');
                    }
                }
            }
            else{
                newActions = [...actions, { type, value, id: uniqueId(), meta }];
                newActionValue = null;
                newSelectedAction = null;
            }
            const tags = calcTags(tagsStat);

            return this.field({
                addedActions: newActions,
                selectedAction: newSelectedAction,
                actionValue: newActionValue,
                availableTags: tags.available,
                suggestedTags: tags.suggested,
            });
        });
        this.register<string>(names.REMOVE_ACTION, e => {
            const actionId = e.payload;
            assertString(actionId);
            
            const {
                ids = [],
                addedActions: actions = [],
            } = this.getState();
            const idx = actions.findIndex(a => a.id === actionId);
            assertTrue(idx >= 0);

            const addedActions = [...actions.slice(0, idx), ...actions.slice(idx + 1)];
            return this.field({ addedActions });
        });

        this.register(names.REJECT_DRAFT, e => this.getDefaultState());
    }

    private pressureLevels(levels: Partial<PressureLevels>)
    {
        const newLevels = {...this.state.actionValue as PressureLevels, ...levels};
        return this.field({ actionValue: newLevels, isGroupActionValid: isPressureLevelsValid(newLevels) });
    }

    private temperatureLevels(levels: Partial<TemperatureLevels>)
    {
        const newLevels = {...this.state.actionValue as TemperatureLevels, ...levels};
        return this.field({ actionValue: newLevels, isGroupActionValid: isTemperatureLevelsValid(newLevels) });
    }

    private getDefaultActionValue(type: ActionType)
    {
        switch(type) {
            case ActionType.SetPressure:
                return {
                    DefineAlertLevels: false,
                };
            case ActionType.SetTemperature:
                return {};
            case ActionType.AddTags:
            case ActionType.RemoveTags:
                return [];
            default:
                return null;
        }
    }

    private getDefaultState(): VehicleBatchEditStoreState
    {
        return {
            ids: [],

            allTags: [],
            tagsStat: new Map(),
            availableTags: [],
            suggestedTags: [],
            
            addedActions: [],
            
            unit: null,
            selectedAction: null,
            actionValue: null,
            axleCount: 0,

            isGroupActionValid: false,
        };
    }

    isValid(): boolean
    {
        let { ids, addedActions } = this.state;
        if(ids.length == 0){
            ids = JSON.parse(localStorage.getItem('groupActionVehicleIds'));
        }
        return Array.isArray(ids) && ids.length > 0 && Array.isArray(addedActions) && addedActions.length > 0;
    }
}

function isPressureLevelsValid(levels: PressureLevels)
{
    const { Axle, Baseline } = levels;
    return Baseline > 0;
}

function isTemperatureLevelsValid(levels: TemperatureLevels)
{
    const { Axle, AbsLevelMaximumRed, AbsLevelMaximumYellow, AbsLevelMinimumRed, AbsLevelMinimumYellow } = levels;
    return (typeof Axle === 'number'); // && (isNumber(AbsLevelMaximumRed) || isNumber(AbsLevelMaximumYellow) || isNumber(AbsLevelMinimumRed) || isNumber(AbsLevelMinimumYellow));
}

function calcTags(stat: Map<string, number>): Tags
{
    const available = [...stat].sort((a, b) => b[1]- a[1]).map(kv => kv[0]);
    const suggested = available.slice(0, 5);
    return { available, suggested };
}

interface Tags
{
    available: string[];
    suggested: string[];
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/Vehicle/BatchEdit/flux/VehicleBatchEditStore.ts